import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import { authApi, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

export default function AdminAvisos() {
  const { user } = useAppUser() || {};
  const location = useLocation();
  const [avisos, setAvisos] = useState(null);
  const [avisoForm, setAvisoForm] = useState({ title: "", message: "" });
  const [posting, setPosting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    setFormOpen(!!new URLSearchParams(location.search).get("novo"));
  }, [location.search]);

  const refreshAvisos = async () => {
    const res = await authApi().get("/announcements");
    setAvisos(res.data.announcements);
  };

  useEffect(() => {
    if (user) refreshAvisos();
  }, [user]);

  const postAviso = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      await authApi().post("/announcements", avisoForm);
      toast.success("Aviso publicado para os alunos.");
      setAvisoForm({ title: "", message: "" });
      setFormOpen(false);
      await refreshAvisos();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  return (
    <AppShell active="avisos" requireAdmin>
      <section data-testid="admin-announcements-section">
        <h1 className="font-display text-2xl font-bold" data-testid="admin-avisos-title">Avisos</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6" data-testid="admin-announcements-label">
          VISÍVEIS PARA TODOS OS USUÁRIOS {avisos ? `(${avisos.length})` : ""}
        </p>

        {formOpen && (
          <form onSubmit={postAviso} className="bg-surface border border-white/10 rounded-2xl p-6 space-y-4 mb-6" data-testid="admin-announcement-form">
            <input required minLength={2} value={avisoForm.title} onChange={(e) => setAvisoForm((f) => ({ ...f, title: e.target.value }))} placeholder="Título do aviso" className={inputCls} data-testid="announcement-title-input" />
            <textarea required minLength={2} rows={3} value={avisoForm.message} onChange={(e) => setAvisoForm((f) => ({ ...f, message: e.target.value }))} placeholder="Mensagem para alunos, instrutores e liderança" className={`${inputCls} resize-none`} data-testid="announcement-message-input" />
            <div className="flex gap-3 flex-wrap">
              <button type="submit" disabled={posting} data-testid="announcement-submit-button" className="inline-flex items-center justify-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-all duration-300">
                <Megaphone size={18} /> {posting ? "Publicando..." : "Publicar Aviso"}
              </button>
              <button type="button" onClick={() => setFormOpen(false)} data-testid="announcement-form-close" className="font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-8 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
                FECHAR
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3" data-testid="admin-announcements-list">
          {(avisos || []).map((a, i) => (
            <div key={a.id} className="bg-surface border border-white/10 rounded-xl px-5 py-4" data-testid={`admin-announcement-${i}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-display font-semibold">{a.title}</p>
                <span className="font-mono text-xs text-zinc-500">{new Date(a.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-1.5">{a.message}</p>
            </div>
          ))}
        </div>
      </section>
    </AppShell>
  );
}
